import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useInvoicePdfCache } from "@/providers/invoice-pdf-cache";
import { useTenant } from "@/providers/session";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Count } from "@/components/shared/data-table/total-count";
import { SimpleInvoiceViewer } from "@/components/shared/file-viewers/simple-invoice-viewer";
import { InvoiceComments } from "@/components/shared/invoice-comments";
import { Button } from "@/components/ui/button";
import { useGetAllAccounts } from "@/queries/accounts/get-all";
import { useGetAllPastBookings } from "@/queries/past-bookings/get-all";
import { SquareArrowOutUpRight } from "lucide-react";
import { PastBookings } from "./past-bookings";

export enum InfoPanelTab {
  PAST_BOOKINGS = "past_bookings",
  INVOICE = "invoice",
  COMMENTS = "comments",
}

type Props = {
  invoiceId?: string;
  supplier?: {
    id: string;
    name: string;
    no: string;
  };
  accountId?: string;
  lineItemId?: string;
  currentTab: InfoPanelTab;
  setCurrentTab: (tab: InfoPanelTab) => void;
};

export const InfoPanel = ({
  invoiceId,
  supplier,
  accountId,
  lineItemId,
  currentTab,
  setCurrentTab,
}: Props) => {
  const { accounts } = useGetAllAccounts();
  const { data: invoices, loading: invoicesLoading } = useGetAllPastBookings({
    supplierId: supplier?.id,
    accountId,
  });

  const { companySlug } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();

  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const _ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const { fetchPdf } = useInvoicePdfCache();

  useEffect(() => {
    invoiceId && fetchPdf(invoiceId);
  }, [invoiceId, fetchPdf]);

  const handleOnExpand = () => {
    navigate({
      to: "/$company/buchhaltung/rechnung/$id",
      params: { company: companySlug, id: invoiceId as string },
      search: { returnTo: location.href },
    });
  };

  useEffect(() => {
    const node = _ref.current;

    if (!node) return;

    const resizeObserver = new ResizeObserver(([e]) => {
      const _width = e.contentRect.width;
      const _height = e.contentRect.height;

      !width && setWidth(_width);
      !height && setHeight(_height);
    });

    resizeObserver.observe(node);

    return () => resizeObserver.disconnect();
  }, []);

  const account = accounts.find((account) => account.accountId === accountId);

  return (
    <div ref={_ref} className="h-full p-4 overflow-auto">
      <div className="h-full">
        <Tabs
          value={currentTab}
          onValueChange={(value) => setCurrentTab(value as InfoPanelTab)}
          className="flex-1 flex flex-col h-full"
        >
          <div className="flex flex-row gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleOnExpand}
              className="gap-2"
              disabled={!invoiceId}
            >
              <SquareArrowOutUpRight className="w-4 h-4" />
              {t("open_invoice")}
            </Button>
            <TabsList className="grid grid-cols-3">
              <TabsTrigger value={InfoPanelTab.INVOICE} disabled={!invoiceId}>
                {t("fastlane.info_panel.tabs.invoice")}
              </TabsTrigger>
              <TabsTrigger value={InfoPanelTab.COMMENTS} className="gap-1">
                {t("fastlane.info_panel.tabs.comments")}
              </TabsTrigger>
              <TabsTrigger value={InfoPanelTab.PAST_BOOKINGS} className="gap-1">
                {t("fastlane.info_panel.tabs.past_bookings")}
                {invoicesLoading ? (
                  "..."
                ) : (
                  <Count count={invoices?.length ?? 0} noSuccess />
                )}
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value={InfoPanelTab.INVOICE} className="h-full">
            <SimpleInvoiceViewer invoiceId={invoiceId} height={height - 50} />
          </TabsContent>
          <TabsContent
            value={InfoPanelTab.PAST_BOOKINGS}
            className="h-full overflow-auto"
          >
            <PastBookings
              supplier={supplier}
              account={{
                id: account?.accountId ?? "",
                name: account?.accountName ?? "",
                no: account?.accountNo ?? "",
              }}
              lineItemId={lineItemId}
              invoiceId={invoiceId}
              width={width}
            />
          </TabsContent>
          <TabsContent value={InfoPanelTab.COMMENTS} className="h-full">
            {invoiceId && <InvoiceComments invoiceId={invoiceId} />}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
